import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import priceMovementsService from '../../utils/services/priceMovementsService';
import {
    BookmakerLogo,
    BOOKMAKER_DISPLAY_NAMES,
} from '../HotProps/bookmakerAssets';
import PriceHistoryChart from '../HotProps/PriceHistoryChart';
import PriceSparkline from '../HotProps/PriceSparkline';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatOdds(v) {
    if (v == null) return '—';
    return Number(v).toFixed(2);
}

function formatPercent(v, { showSign = true } = {}) {
    if (v == null || Number.isNaN(Number(v))) return '—';
    const n = Number(v);
    const sign = n > 0 ? '+' : '';
    return `${showSign ? sign : ''}${n.toFixed(2)}%`;
}

function formatStartTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const dayMs = 24 * 60 * 60 * 1000;
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const diffDays = Math.floor((d.getTime() - todayStart) / dayMs);
    if (diffDays === 0) return `Today ${timeStr}`;
    if (diffDays === 1) return `Tomorrow ${timeStr}`;
    if (diffDays === -1) return `Yesterday ${timeStr}`;
    const dateStr = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${dateStr} ${timeStr}`;
}

function formatTickTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

// Numeric portion of a market label (e.g. `25+ Disposals` → 25).
function parseMarketNumber(market) {
    const m = (market || '').match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
}

// Short form for the threshold pill (e.g. `25+ Disposals` → `25+`).
function shortThreshold(market) {
    const m = (market || '').match(/(\d+\+)/);
    return m ? m[1] : market;
}

// ─── per-bookmaker movement row + nested tick history ───────────────────────

function TickHistoryTable({ priceChanges }) {
    if (!Array.isArray(priceChanges) || priceChanges.length === 0) {
        return (
            <div className="px-3 py-3 text-center text-[10px] font-mono text-slate-600">
                No ticks recorded.
            </div>
        );
    }

    const reversed = [...priceChanges].reverse();
    const lastIndex = reversed.length - 1;

    return (
        <div className="max-h-[220px] overflow-auto styled-scrollbar">
            <table className="w-full text-left">
                <thead className="sticky top-0 bg-slate-950/95 backdrop-blur-sm">
                    <tr className="border-b border-slate-800/60">
                        <th className="px-3 py-1.5 text-[9px] font-mono font-semibold text-slate-600 uppercase tracking-widest">#</th>
                        <th className="px-3 py-1.5 text-[9px] font-mono font-semibold text-slate-600 uppercase tracking-widest">Time</th>
                        <th className="px-3 py-1.5 text-[9px] font-mono font-semibold text-slate-600 uppercase tracking-widest text-right">Price</th>
                    </tr>
                </thead>
                <tbody>
                    {reversed.map((tick, i) => {
                        const isOpen = i === lastIndex;
                        const isCurrent = i === 0;
                        return (
                            <tr
                                key={`${tick.price_id ?? i}-${tick.timestamp}`}
                                className="border-b border-slate-800/30 last:border-0 hover:bg-slate-800/20 transition-colors"
                            >
                                <td className="px-3 py-1.5 text-[10px] font-mono text-slate-700 tabular-nums">
                                    {tick.price_id != null ? `#${tick.price_id}` : '—'}
                                </td>
                                <td className="px-3 py-1.5 text-[10px] font-mono text-slate-400 tabular-nums whitespace-nowrap">
                                    <span className="inline-flex items-center gap-1.5">
                                        {formatTickTime(tick.timestamp)}
                                        {isCurrent && (
                                            <span className="text-[8px] uppercase tracking-wider text-emerald-400 px-1 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/5">
                                                current
                                            </span>
                                        )}
                                        {isOpen && !isCurrent && (
                                            <span className="text-[8px] uppercase tracking-wider text-amber-400 px-1 py-0.5 rounded border border-amber-500/30 bg-amber-500/5">
                                                open
                                            </span>
                                        )}
                                    </span>
                                </td>
                                <td className={`px-3 py-1.5 text-[11px] font-mono font-semibold tabular-nums text-right ${
                                    isCurrent ? 'text-emerald-400' : 'text-slate-200'
                                }`}>
                                    {formatOdds(tick.price)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function BookmakerMovementItem({ bk, isBest }) {
    const [ticksOpen, setTicksOpen] = useState(false);
    const positive = (bk.percentage_change || 0) > 0;
    const sparkColor = positive ? '#34d399' : '#f87171';
    const tickCount = Array.isArray(bk.price_changes) ? bk.price_changes.length : 0;
    const canExpand = tickCount > 0;

    return (
        <div>
            <button
                type="button"
                onClick={() => canExpand && setTicksOpen((v) => !v)}
                disabled={!canExpand}
                aria-expanded={ticksOpen}
                className={`w-full grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-3 py-2 text-left transition-colors ${
                    canExpand ? 'hover:bg-slate-800/40 cursor-pointer' : 'cursor-default'
                } ${ticksOpen ? 'bg-slate-800/30' : ''}`}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <BookmakerLogo bookmaker={bk.bookmaker} size={18} />
                    <span className="text-xs font-mono text-slate-300 truncate">
                        {BOOKMAKER_DISPLAY_NAMES[bk.bookmaker] || bk.bookmaker}
                    </span>
                    {isBest && (
                        <span className="text-[9px] font-mono text-emerald-400/70 uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-500/5 border border-emerald-500/20 whitespace-nowrap">
                            best
                        </span>
                    )}
                </div>

                <div className="hidden sm:flex items-center gap-1 font-mono tabular-nums text-[11px]">
                    <span className="text-slate-500">{formatOdds(bk.open_price)}</span>
                    <svg className="w-3 h-3 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                    <span className={`${positive ? 'text-emerald-400' : 'text-red-400'} font-semibold`}>
                        {formatOdds(bk.current_price)}
                    </span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="hidden md:block">
                        <PriceSparkline
                            priceChanges={bk.price_changes}
                            color={sparkColor}
                            width={80}
                            height={20}
                        />
                    </div>
                    <span
                        className={`text-[11px] font-mono font-semibold tabular-nums px-1.5 py-0.5 rounded whitespace-nowrap ${
                            positive ? 'text-emerald-400 bg-emerald-500/5' : 'text-red-400 bg-red-500/5'
                        }`}
                    >
                        {formatPercent(bk.percentage_change)}
                    </span>
                    <span className="hidden lg:inline text-[9px] font-mono text-slate-700 whitespace-nowrap tabular-nums">
                        {tickCount}t
                    </span>
                </div>

                <svg
                    className={`w-3 h-3 transition-all ${
                        canExpand
                            ? ticksOpen ? 'rotate-180 text-emerald-400' : 'text-slate-600'
                            : 'text-slate-800'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {ticksOpen && canExpand && (
                <div className="border-t border-slate-800/60 bg-slate-950/80 animate-fade-in">
                    <TickHistoryTable priceChanges={bk.price_changes} />
                </div>
            )}
        </div>
    );
}

// ─── summary stripe ─────────────────────────────────────────────────────────

function SummaryStripe({ prop, sortedHistory }) {
    const best = prop?.best_odds;
    const biggestMover = sortedHistory[0];
    const moverPositive = biggestMover && (biggestMover.percentage_change || 0) > 0;

    // Best open: the bookmaker that started with the highest decimal open_price.
    // This is the historical peak payout — useful to compare against the current
    // best available to understand whether the line opened sharp or has drifted.
    const bestOpen = useMemo(() => {
        if (!sortedHistory.length) return null;
        return sortedHistory.reduce((acc, curr) => {
            const currOpen = curr.open_price;
            if (currOpen == null) return acc;
            if (!acc || currOpen > acc.open_price) return curr;
            return acc;
        }, null);
    }, [sortedHistory]);

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {/* Best open card */}
            <div className="rounded-lg border border-slate-800/80 bg-slate-950/40 px-3.5 py-2.5">
                <div className="text-[9px] font-mono text-slate-600 uppercase tracking-[0.16em] mb-1.5">
                    Best open price
                </div>
                {bestOpen ? (
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <BookmakerLogo bookmaker={bestOpen.bookmaker} size={20} />
                            <span className="text-xs font-mono text-slate-300 truncate">
                                {BOOKMAKER_DISPLAY_NAMES[bestOpen.bookmaker] || bestOpen.bookmaker}
                            </span>
                        </div>
                        <div className="flex items-baseline gap-2 flex-shrink-0">
                            <span className="text-[18px] font-mono font-bold text-white tabular-nums leading-none">
                                {formatOdds(bestOpen.open_price)}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="text-[11px] font-mono text-slate-600">No open price recorded.</div>
                )}
            </div>

            {/* Best available card */}
            <div className="rounded-lg border border-slate-800/80 bg-slate-950/40 px-3.5 py-2.5">
                <div className="text-[9px] font-mono text-slate-600 uppercase tracking-[0.16em] mb-1.5">
                    Best available
                </div>
                {best ? (
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <BookmakerLogo bookmaker={best.bookmaker} size={20} />
                            <span className="text-xs font-mono text-slate-300 truncate">
                                {BOOKMAKER_DISPLAY_NAMES[best.bookmaker] || best.bookmaker}
                            </span>
                        </div>
                        <div className="flex items-baseline gap-2 flex-shrink-0">
                            <span className="text-[18px] font-mono font-bold text-emerald-400 tabular-nums leading-none">
                                {formatOdds(best.odds)}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="text-[11px] font-mono text-slate-600">No best price available.</div>
                )}
            </div>

            {/* Biggest mover card */}
            <div className="rounded-lg border border-slate-800/80 bg-slate-950/40 px-3.5 py-2.5">
                <div className="text-[9px] font-mono text-slate-600 uppercase tracking-[0.16em] mb-1.5">
                    Biggest mover
                </div>
                {biggestMover ? (
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            <BookmakerLogo bookmaker={biggestMover.bookmaker} size={20} />
                            <span className="text-xs font-mono text-slate-300 truncate">
                                {BOOKMAKER_DISPLAY_NAMES[biggestMover.bookmaker] || biggestMover.bookmaker}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 font-mono tabular-nums">
                            <span className="text-[12px] text-slate-500">{formatOdds(biggestMover.open_price)}</span>
                            <svg className="w-3 h-3 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                            <span className={`text-[14px] font-bold ${moverPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                                {formatOdds(biggestMover.current_price)}
                            </span>
                            <span
                                className={`ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                    moverPositive ? 'text-emerald-400 bg-emerald-500/10' : 'text-red-400 bg-red-500/10'
                                }`}
                            >
                                {formatPercent(biggestMover.percentage_change)}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="text-[11px] font-mono text-slate-600">No movement recorded.</div>
                )}
            </div>
        </div>
    );
}

// ─── prop content (active threshold body) ───────────────────────────────────

function PropContent({ prop }) {
    const sortedHistory = useMemo(() => {
        const history = Array.isArray(prop?.price_history) ? prop.price_history : [];
        return [...history].sort((a, b) => (b.percentage_change || 0) - (a.percentage_change || 0));
    }, [prop]);
    const best = prop?.best_odds || null;

    if (!prop) return null;

    if (sortedHistory.length === 0) {
        return (
            <div className="space-y-3">
                <SummaryStripe prop={prop} sortedHistory={sortedHistory} />
                <div className="rounded-lg border border-slate-800/80 bg-slate-950/40 py-10 px-6 flex flex-col items-center text-center">
                    <div className="w-10 h-10 rounded-lg bg-slate-800/60 border border-slate-700/50 flex items-center justify-center mb-3 text-slate-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l4-4 4 6 4-8 4 6 2-2" />
                        </svg>
                    </div>
                    <p className="text-xs font-medium text-slate-400 mb-1">No movements recorded</p>
                    <p className="text-[11px] font-mono text-slate-600 leading-relaxed max-w-[320px]">
                        The bot is tracking this market but no bookmaker has changed its price since tracking began.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <SummaryStripe prop={prop} sortedHistory={sortedHistory} />

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-4">
                {/* Per-book breakdown */}
                <div>
                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <span>Per-book movement</span>
                        <span className="h-px flex-1 bg-slate-800/60" />
                        <span className="text-slate-600">
                            {sortedHistory.length} {sortedHistory.length === 1 ? 'book' : 'books'}
                        </span>
                    </div>
                    <div className="rounded-lg border border-slate-800/60 bg-slate-950/60 divide-y divide-slate-800/50 overflow-hidden">
                        {sortedHistory.map((bk) => (
                            <BookmakerMovementItem
                                key={bk.bookmaker}
                                bk={bk}
                                isBest={best && best.bookmaker === bk.bookmaker}
                            />
                        ))}
                    </div>
                    <p className="mt-1.5 text-[9px] font-mono text-slate-700 tracking-wide">
                        Tap a bookmaker to inspect every recorded price.
                    </p>
                </div>

                {/* Overlaid price history chart */}
                <div>
                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <span>Price history</span>
                        <span className="h-px flex-1 bg-slate-800/60" />
                    </div>
                    <div className="rounded-lg border border-slate-800/60 bg-slate-950/60 p-3">
                        <PriceHistoryChart
                            priceHistory={sortedHistory}
                            bookmakerNames={BOOKMAKER_DISPLAY_NAMES}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── inline status panels (loading / error / empty) ────────────────────────

function StatusPanel({ children, accent = 'slate' }) {
    const accentMap = {
        slate: 'border-slate-700/50 bg-slate-800/40 text-slate-400',
        emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300',
        amber: 'border-amber-500/30 bg-amber-500/5 text-amber-300',
        red: 'border-red-500/30 bg-red-500/5 text-red-300',
    };
    return (
        <div className={`rounded-lg border ${accentMap[accent] || accentMap.slate} px-4 py-6 flex flex-col items-center text-center`}>
            {children}
        </div>
    );
}

function ErrorPanel({ error, onRetry }) {
    if (error?.isSubscriptionError) {
        return (
            <StatusPanel accent="emerald">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-3 text-emerald-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </div>
                <p className="text-sm font-semibold text-white mb-1">Subscription Required</p>
                <p className="text-[11px] font-mono text-slate-500 leading-relaxed mb-4 max-w-[360px]">
                    A Snipe Pro or Snipe Elite subscription is required to view price movement history.
                </p>
                <a
                    href="/manage-subscriptions"
                    className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-xs font-semibold hover:from-emerald-500 hover:to-teal-500 transition-all flex items-center gap-2"
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Manage Subscriptions
                </a>
            </StatusPanel>
        );
    }

    const isRateLimit = error?.errorCode === 'RATE_LIMIT_EXCEEDED';
    const isNoData = error?.errorCode === 'NO_DATA' || error?.isNoData;
    const isService = error?.errorCode === 'SERVICE_UNAVAILABLE';
    const accent = isRateLimit ? 'amber' : isNoData ? 'slate' : isService ? 'slate' : 'red';
    const title = isRateLimit
        ? 'Too Many Requests'
        : isNoData
            ? 'No Data Yet'
            : isService
                ? 'Temporarily Unavailable'
                : error?.isAuthError
                    ? 'Authentication Failed'
                    : 'Error';

    return (
        <StatusPanel accent={accent}>
            <p className="text-sm font-semibold text-white mb-1">{title}</p>
            <p className="text-[11px] font-mono text-slate-500 leading-relaxed mb-3 max-w-[360px]">
                {error?.message || 'Something went wrong.'}
            </p>
            {!error?.isAuthError && (
                <button
                    type="button"
                    onClick={onRetry}
                    className="px-3 py-1.5 text-[11px] font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all flex items-center gap-1.5"
                >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Retry
                </button>
            )}
            {isRateLimit && (
                <p className="mt-2 text-[10px] font-mono text-slate-700">Resets every 60s</p>
            )}
        </StatusPanel>
    );
}

// ─── main modal ────────────────────────────────────────────────────────────

const PROP_TYPE_LABEL = {
    disposals: 'Disposals',
    goals: 'Goals',
    marks: 'Marks',
    tackles: 'Tackles',
    fantasy_points: 'Fantasy Points',
};

const PROP_TYPE_OPTIONS = [
    { value: 'disposals', label: 'Disposals' },
    { value: 'goals', label: 'Goals' },
    { value: 'marks', label: 'Marks' },
    { value: 'tackles', label: 'Tackles' },
    { value: 'fantasy_points', label: 'Fantasy Points' },
];

// Backend cache refreshes every 60 seconds — polling matches that cadence.
const REFRESH_SECONDS = 60;

function PriceMovementsModal({
    open,
    player,
    propType,
    initialMarket,
    matchLabel,
    startTime,
    sport = 'afl',
    onClose,
    onAuthError,
    onPropTypeChange,
}) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeMarket, setActiveMarket] = useState(initialMarket || null);
    const [countdown, setCountdown] = useState(REFRESH_SECONDS);

    // Stash the parent callbacks in refs so the fetch/effect identities don't
    // change each time the parent re-renders (OddsScreen re-renders every
    // second from its countdown). Without this, an inline `onAuthError`/
    // `onClose` from the parent would invalidate `fetchData` on every tick
    // and re-fire the network request — i.e. the request-spam bug.
    const onCloseRef = useRef(onClose);
    const onAuthErrorRef = useRef(onAuthError);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
    useEffect(() => { onAuthErrorRef.current = onAuthError; }, [onAuthError]);

    // Reset transient state every time a new player/prop_type combo is opened.
    useEffect(() => {
        if (!open) return;
        setData(null);
        setError(null);
        setActiveMarket(initialMarket || null);
    }, [open, player, propType, initialMarket]);

    // Body scroll lock + Escape-to-close while open. Reads the latest onClose
    // through a ref so the listener isn't reattached on every parent render.
    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => {
            if (e.key === 'Escape') onCloseRef.current?.();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKey);
        };
    }, [open]);

    // `silent` mode is used by the auto-refresh interval so the user keeps
    // seeing their data while a background poll is in flight. On a silent
    // failure we deliberately don't blank out the data or flip into the error
    // panel — staleness is preferable to a flicker. Auth errors still surface
    // through `onAuthError` regardless, so an expired session doesn't go
    // unnoticed.
    const fetchData = useCallback(async ({ silent = false } = {}) => {
        if (!open || !player || !propType) return;
        setCountdown(REFRESH_SECONDS);
        if (!silent) {
            setIsLoading(true);
            setError(null);
        }
        const result = await priceMovementsService.getPriceMovements({
            player_name: player,
            prop_type: propType,
            sport,
        });
        if (result.success) {
            setData(result.data);
            setError(null);
        } else if (!silent) {
            setData(null);
            setError(result);
        }
        if (!result.success && result.isAuthError && typeof onAuthErrorRef.current === 'function') {
            onAuthErrorRef.current(result);
        }
        if (!silent) setIsLoading(false);
    }, [open, player, propType, sport]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Auto-refresh while open. Two intervals: a 1s tick that drives the
    // countdown indicator, and a 60s interval that fires the silent refresh.
    // The cleanup in `return () => …` ensures both intervals are torn down
    // the moment the modal closes (or when player/propType/sport change), so
    // we never poll for a closed modal.
    useEffect(() => {
        if (!open || !player || !propType) return undefined;

        const tickId = setInterval(() => {
            setCountdown((prev) => (prev <= 1 ? REFRESH_SECONDS : prev - 1));
        }, 1000);

        const refreshId = setInterval(() => {
            fetchData({ silent: true });
        }, REFRESH_SECONDS * 1000);

        return () => {
            clearInterval(tickId);
            clearInterval(refreshId);
        };
    }, [open, player, propType, sport, fetchData]);

    // Sort thresholds ascending by numeric value (15+, 20+, 25+, …).
    const sortedProps = useMemo(() => {
        const props = Array.isArray(data?.props) ? data.props : [];
        return [...props].sort((a, b) => parseMarketNumber(a.market) - parseMarketNumber(b.market));
    }, [data]);

    // When data lands, seed a sensible active threshold. We use the functional
    // form of setState (instead of depending on `activeMarket`) so a user-
    // initiated tab click doesn't re-run this effect and snap back to
    // initialMarket. If the user already has a valid selection, keep it.
    useEffect(() => {
        if (!sortedProps.length) {
            setActiveMarket(null);
            return;
        }
        setActiveMarket((prev) => {
            if (prev && sortedProps.find((p) => p.market === prev)) return prev;
            const initialMatch = sortedProps.find((p) => p.market === initialMarket);
            return initialMatch ? initialMatch.market : sortedProps[0].market;
        });
    }, [sortedProps, initialMarket]);

    const activeProp = useMemo(
        () => sortedProps.find((p) => p.market === activeMarket) || null,
        [sortedProps, activeMarket]
    );

    if (!open) return null;

    const propTypeLabel = PROP_TYPE_LABEL[propType] || propType;

    return createPortal(
        <div
            className="fixed inset-0 flex items-end md:items-center justify-center p-0 md:p-6 bg-slate-950/80 backdrop-blur-sm animate-fade-in"
            style={{ zIndex: 10000 }}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={`Price movements for ${player}`}
        >
            <div
                className="relative w-full md:max-w-5xl bg-slate-900 border-t md:border border-slate-800 rounded-t-2xl md:rounded-2xl shadow-2xl shadow-black/60 overflow-hidden flex flex-col max-h-[90vh] animate-slide-up md:animate-fade-in"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0px)' }}
            >
                {/* Mobile grab handle */}
                <div aria-hidden="true" className="md:hidden flex justify-center pt-2">
                    <span className="block w-10 h-1 rounded-full bg-slate-700/80" />
                </div>

                {/* Top emerald hairline + subtle glow (desktop only) */}
                <div className="hidden md:block absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-20 bg-emerald-500/10 blur-3xl pointer-events-none" />

                {/* Header */}
                <div className="relative flex items-start justify-between px-5 pt-3 md:pt-5 pb-4 border-b border-slate-800/80 gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-slate-800/60 border border-slate-700/50 flex items-center justify-center text-emerald-400 flex-shrink-0">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-sm md:text-[15px] font-semibold text-white tracking-tight truncate">
                                {player}
                            </h3>
                            <p className="text-[10px] md:text-[11px] font-mono text-slate-500 uppercase tracking-[0.14em] truncate">
                                <span className="text-emerald-500/80">{propTypeLabel}</span>
                                {matchLabel && (
                                    <>
                                        <span className="text-slate-700"> · </span>
                                        <span className="normal-case tracking-normal">{matchLabel}</span>
                                    </>
                                )}
                                {startTime && (
                                    <>
                                        <span className="text-slate-700"> · </span>
                                        <span className="normal-case tracking-normal text-slate-600">
                                            {formatStartTime(startTime)}
                                        </span>
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {!isLoading && !error && data && (
                            <div
                                className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded bg-slate-800/40 border border-slate-700/40"
                                title="Auto-refreshes every 60 seconds while open"
                            >
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-50" />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                                </span>
                                <span className="text-[10px] font-mono text-slate-400 tabular-nums">
                                    {countdown}s
                                </span>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close price movements"
                            className="w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:text-white hover:bg-slate-800/70 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Threshold tabs + prop-type switcher (only when we have data) */}
                {!isLoading && !error && (
                    <div className="px-5 py-2.5 border-b border-slate-800/60 flex items-center gap-3">
                        <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-x-auto styled-scrollbar">
                            {sortedProps.length > 0 && (
                                <span className="text-[9px] font-mono font-semibold text-slate-600 uppercase tracking-[0.18em] mr-1 flex-shrink-0">
                                    Line
                                </span>
                            )}
                            {sortedProps.map((p) => {
                                const selected = p.market === activeMarket;
                                return (
                                    <button
                                        key={p.market}
                                        type="button"
                                        onClick={() => setActiveMarket(p.market)}
                                        className={`relative flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded text-[11px] font-mono transition-all duration-150 ${
                                            selected
                                                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
                                                : 'text-slate-400 hover:text-slate-200 border border-slate-800 hover:border-slate-700 bg-slate-900/40'
                                        }`}
                                    >
                                        <span className="font-semibold tabular-nums">{shortThreshold(p.market)}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {typeof onPropTypeChange === 'function' && (
                            <div className="relative flex-shrink-0">
                                <select
                                    value={propType || ''}
                                    onChange={(e) => onPropTypeChange(e.target.value)}
                                    aria-label="Prop type"
                                    className="appearance-none pl-3 pr-7 py-1 bg-slate-900/80 border border-slate-800 rounded text-[11px] font-mono text-slate-300 cursor-pointer hover:border-slate-700 hover:text-white focus:outline-none focus:border-emerald-500/50 transition-colors"
                                >
                                    {PROP_TYPE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                <svg
                                    aria-hidden="true"
                                    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        )}
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-auto styled-scrollbar px-4 md:px-5 py-4">
                    {isLoading && !data && (
                        <div className="py-16 flex flex-col items-center text-center">
                            <div className="flex gap-1 mb-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '0ms' }} />
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '200ms' }} />
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '400ms' }} />
                            </div>
                            <span className="text-xs font-mono text-slate-500">Loading price history</span>
                        </div>
                    )}

                    {!isLoading && error && (
                        <ErrorPanel error={error} onRetry={fetchData} />
                    )}

                    {!isLoading && !error && data && sortedProps.length === 0 && (
                        <StatusPanel accent="slate">
                            <div className="w-10 h-10 rounded-lg bg-slate-800/60 border border-slate-700/50 flex items-center justify-center mb-3 text-slate-500">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l4-4 4 6 4-8 4 6 2-2" />
                                </svg>
                            </div>
                            <p className="text-sm font-semibold text-slate-300 mb-1">No movements yet</p>
                            <p className="text-[11px] font-mono text-slate-500 leading-relaxed max-w-[340px]">
                                The bot is tracking {player}'s {propTypeLabel.toLowerCase()} markets but no bookmaker has changed a price since tracking started.
                            </p>
                        </StatusPanel>
                    )}

                    {!isLoading && !error && activeProp && (
                        <PropContent prop={activeProp} />
                    )}
                </div>

            </div>
        </div>,
        document.body
    );
}

export default PriceMovementsModal;
